import { DebugConfig, DebugFeature } from '../types/config';
import { redactSensitiveKeys } from './redactSensitiveKeys';

type DebugPayload = () => unknown;

/**
 * Per-feature debug logger, configured from the `debug` object in
 * `config.json` (build-independent, everything off by default). A module
 * singleton on purpose — most call sites are plain non-React utils.
 * Payload thunks defer only the payload itself; gate expensive payload-input
 * collection on hot paths with `isEnabled()`. Design rationale and the
 * `@debugFeature` documentation convention live in docs/debug.md.
 */
class DebugLogger {
  private flags: DebugConfig = {};

  configure(flags: DebugConfig | undefined) {
    this.flags = flags ?? {};
  }

  /** Guard for call sites whose payload INPUTS are expensive to collect on hot paths — the payload thunk itself is already lazy. */
  isEnabled(feature: DebugFeature) {
    return this.flags[feature] === true;
  }

  log(feature: DebugFeature, message: string, payload?: DebugPayload) {
    if (!this.isEnabled(feature)) {
      return;
    }

    if (payload) {
      // Payloads may carry whole request-data contexts — plain-object keys
      // like sessionId/token are redacted centrally before reaching the console.
      try {
        console.debug(
          `[debug:${feature}]`,
          message,
          redactSensitiveKeys(payload())
        );
      } catch (error) {
        // A debug log must never throw into the caller (payload getters may throw).
        console.debug(`[debug:${feature}]`, message, '<payload failed>', error);
      }
      return;
    }

    console.debug(`[debug:${feature}]`, message);
  }

  /** Returns `log` bound to one feature, for modules logging a single area. */
  scope(feature: DebugFeature) {
    return (message: string, payload?: DebugPayload) =>
      this.log(feature, message, payload);
  }

  /** Runtime toggles from the browser console, exposed as `window.septimDebug`. */
  enable(feature: DebugFeature) {
    this.flags = { ...this.flags, [feature]: true };
  }

  disable(feature: DebugFeature) {
    this.flags = { ...this.flags, [feature]: false };
  }

  status() {
    return { ...this.flags };
  }
}

export const debugLogger = new DebugLogger();

declare global {
  interface Window {
    // Console escape hatch — see the assignment below.
    septimDebug?: DebugLogger;
  }
}

// Console escape hatch: toggle features at runtime without editing
// config.json and reloading, e.g. `septimDebug.enable('resolveExpressions')`.
if (typeof window !== 'undefined') {
  window.septimDebug = debugLogger;
}
